import React, { useState, useCallback } from 'react';

export function AccountLinking({ shop, onAccountLinked }) {
  const [step, setStep] = useState('choose'); // choose, login, register, linking
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  const handleLogin = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          shop,
          source: 'shopify_app'
        })
      });

      const data = await response.json();

      if (data.success) {
        onAccountLinked(data.user, data.session);
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, password, shop, onAccountLinked]);

  const handleRegister = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          shop,
          shopDomain: shop,
          source: 'shopify_app'
        })
      });

      const data = await response.json();

      if (data.success) {
        onAccountLinked(data.user, data.session);
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, password, shop, onAccountLinked]);

  const handleAutoCreate = useCallback(async () => {
    setStep('linking');
    setLoading(true);

    try {
      const response = await fetch('/api/auth/auto-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop,
          autoCreate: true
        })
      });

      const data = await response.json();

      if (data.success) {
        onAccountLinked(data.user, data.session);
      } else {
        setError(data.message || 'Auto-create failed');
        setStep('choose');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setStep('choose');
    } finally {
      setLoading(false);
    }
  }, [shop, onAccountLinked]);

  if (step === 'linking') {
    return (
      <s-page title="RADAR Intelligence">
        <s-section>
          <s-card>
            <s-stack align="center" justify="center">
              <s-stack direction="block" align="center">
                <s-spinner size="large"></s-spinner>
                <s-heading>Connecting your store...</s-heading>
                <s-text tone="subdued">
                  Setting up your RADAR account and linking {shop}
                </s-text>
              </s-stack>
            </s-stack>
          </s-card>
        </s-section>
      </s-page>
    );
  }

  return (
    <s-page title="Connect to RADAR Intelligence">
      <s-section>
        <s-card>
          <s-stack direction="block" gap="large">
            <s-stack direction="block">
              <s-heading>Welcome to RADAR!</s-heading>
              <s-text tone="subdued">
                Connect your Shopify store ({shop}) to your RADAR account to unlock
                powerful competitive intelligence features.
              </s-text>
            </s-stack>

            {error && (
              <s-banner status="critical" title="Error">
                <p>{error}</p>
              </s-banner>
            )}

            {step === 'choose' && (
              <s-stack direction="block" gap="large">
                <s-stack direction="inline" justify="space-between">
                  <s-button
                    variant="primary"
                    size="large"
                    onClick={() => setStep('login')}
                  >
                    I have a RADAR account
                  </s-button>
                  <s-button
                    variant="secondary"
                    size="large"
                    onClick={() => setStep('register')}
                  >
                    Create new account
                  </s-button>
                </s-stack>

                <s-divider></s-divider>

                <s-stack direction="block" align="center">
                  <s-text tone="subdued">Or create account automatically:</s-text>
                  <s-button
                    onClick={handleAutoCreate}
                    disabled={loading}
                  >
                    Auto-setup with store email
                  </s-button>
                  <s-text variant="bodySm" tone="subdued">
                    We'll create an account using your store's contact email
                  </s-text>
                </s-stack>
              </s-stack>
            )}

            {step === 'login' && (
              <s-stack direction="block" gap="base">
                <s-heading>Sign in to RADAR</s-heading>

                <s-text-field
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  autoComplete="email"
                ></s-text-field>

                <s-text-field
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                ></s-text-field>

                <s-stack direction="inline" justify="space-between">
                  <s-button onClick={() => setStep('choose')}>
                    Back
                  </s-button>
                  <s-button
                    variant="primary"
                    onClick={handleLogin}
                    disabled={!email || !password || loading}
                    loading={loading}
                  >
                    Sign In & Connect Store
                  </s-button>
                </s-stack>
              </s-stack>
            )}

            {step === 'register' && (
              <s-stack direction="block" gap="base">
                <s-heading>Create RADAR Account</s-heading>

                <s-text-field
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  autoComplete="email"
                ></s-text-field>

                <s-text-field
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  helpText="Minimum 8 characters"
                  autoComplete="new-password"
                ></s-text-field>

                <s-stack direction="inline" justify="space-between">
                  <s-button onClick={() => setStep('choose')}>
                    Back
                  </s-button>
                  <s-button
                    variant="primary"
                    onClick={handleRegister}
                    disabled={!email || !password || password.length < 8 || loading}
                    loading={loading}
                  >
                    Create Account & Connect Store
                  </s-button>
                </s-stack>
              </s-stack>
            )}
          </s-stack>
        </s-card>
      </s-section>

      <s-section>
        <s-card>
          <s-stack direction="block">
            <s-heading>What happens next?</s-heading>
            <s-stack direction="block" gap="small">
              <s-text>• Your Shopify store will be connected to RADAR</s-text>
              <s-text>• We'll start analyzing your competitors automatically</s-text>
              <s-text>• You can access the full dashboard at radar.aquivolabs.com</s-text>
              <s-text>• All your store data stays secure and private</s-text>
            </s-stack>
          </s-stack>
        </s-card>
      </s-section>
    </s-page>
  );
}